import { useState, useEffect } from 'react';
import { Fade } from 'react-awesome-reveal';
import { Col, Row, Tooltip } from 'react-bootstrap';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Popover } from 'antd';
import ConfigurableTable from 'components/shared/table/ConfigurableTable';

import {
  AFFILIATIONS_COLUMNS,
  TABLE_NAME_AFFILIATION,
  FLAG,
  PAGINATION,
  INSIGHT_TYPE,
  SORT_BY,
  QUERY_KEY,
  COMPONENT_MAPPING,
} from 'constants/constants';

import {
  exportData,
  getAffiliationsData,
} from 'store/customer/customer-view-actions';
import {
  setSelectedCustomer,
  setSeletedView,
  setInsightType,
  setHcpFlag,
  setFilterInit,
  resetAffiliationFilters,
  setSelectedInsightView,
  setAllInsightView,
  setActiveSectionTab,
} from 'store/customer/customerViewSlice';
import AffiliationsFilters from './AffiliationsFilter';
import styles from './../../shared/table/ConfigurableTable.module.scss';
import { getRestrictedColumn } from 'utils/utils';
import { useNavigate } from 'react-router-dom';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import OverlayIcon from 'components/shared/overlay-icon/OverlayIcon';
import { useQuery } from '@tanstack/react-query';

const Affiliations = () => {
  const [filterExp, setFilterExp] = useState('');
  const [popoverVisible, setPopoverVisible] = useState(false);
  const navigation = useNavigate();

  const affiliationExpression = useSelector(
    (state) => state?.CustomerView?.affiliationsExpression
  );
  const columnRestriction = useSelector(
    (state) => state?.CustomerView?.columnsRestiction
  );
  const activeSectionTab = useSelector(
    (state) => state?.CustomerView?.activeSectionTab
  );

  const handleClick = (rowData, index) => {
    dispatch(setActiveSectionTab(''));
    const payload = {
      value: rowData?.mdmId,
      label: rowData?.siteName,
      zip: rowData?.zip,
      flag: FLAG.site[0],
    };
    dispatch(setFilterInit());
    dispatch(setSeletedView('site'));
    dispatch(setSelectedCustomer(payload));
    dispatch(setInsightType(INSIGHT_TYPE.GENERAL));
    dispatch(setSelectedInsightView({}));
    dispatch(setAllInsightView([]));
    navigation(`/customerView/list/${rowData?.siteName}`);
  };

  const isExporting = useSelector(
    (state) => state?.CustomerView?.exportAffiliation
  );

  const hcpFlag = useSelector((state) => state?.CustomerView.hcpFlag);

  const dispatch = useDispatch();

  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );
  const account = {
    id: selectedCustomer?.value,
    name: selectedCustomer?.label,
  };

  const initailState = {
    values: [],
    offset: 0,
    totalSize: 0,
  };

  const [affiliationData, setAffiliationData] = useState(initailState);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION.defaultPageSize);
  const [sortBy, setSortBy] = useState({
    column: 'siteName',
    order: SORT_BY.ascend,
  });

  const pagination = {
    current: pageIndex,
    total: affiliationData?.totalSize,
    pageSize: pageSize,
    pageSizeOptions: PAGINATION.pageSizeOptions,
    showSizeChanger: true,
    disabledPageSizehandler: true,
  };

  const handleFilterApply = async (filterExp) => {
    setPageIndex(1);
    setFilterExp(filterExp);
  };

  // Fetch affiliations data using react-query
  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.AffiliationsTable,
      selectedCustomer?.value,
      pageIndex,
      pageSize,
      sortBy,
      filterExp,
      hcpFlag,
    ],
    queryFn: async () => {
      const result = await getAffiliationsData(
        selectedCustomer?.value,
        0, // will set actual offset and pageSize once pagination done from backend,
        hcpFlag,
        sortBy.column,
        sortBy.order,
        filterExp
      );
      return result?.values?.length > 0 ? result : initailState;
    },
    enabled:
      activeSectionTab === COMPONENT_MAPPING.Affiliations &&
      filterExp.length > 0
        ? true
        : false,
  });

  // Update local state when query data changes
  useEffect(() => {
    if (data) {
      setAffiliationData(data);
    }
  }, [data]);

  const handleTableChange = (column, order, pagination) => {
    let currentPageSize = pagination.pageSize;

    // Reset page to 1 if page size changed
    if (pageSize !== currentPageSize) {
      setPageIndex(1);
      setPageSize(currentPageSize);
    } else {
      setPageIndex(pagination.current);
    }

    setSortBy({ column, order: SORT_BY[order] });
  };

  const size = { x: 1815, y: 400 };
  const exportTooltip = (
    <Tooltip className='gne-tooltip'>Send to Excel</Tooltip>
  );

  const resetFilters = () => {
    dispatch(resetAffiliationFilters());
  };

  const dowloadData = () => {
    dispatch(
      exportData(
        'Affiliation',
        selectedCustomer?.value,
        hcpFlag,
        affiliationExpression
      )
    );
  };

  const handlePopoverVisibleChange = (visible) => {
    setPopoverVisible(visible);
  };
  const [selectedColumns, setSelectedColumns] = useState(
    AFFILIATIONS_COLUMNS.reduce((result, column) => {
      if (column.isMandatory === 'Y' && column.dataIndex) {
        result.push(column.dataIndex);
      }
      return result;
    }, [])
  );

  const filteredColumns = AFFILIATIONS_COLUMNS.filter((column) =>
    selectedColumns.includes(column.dataIndex)
  );

  useEffect(() => {
    setSelectedColumns(
      AFFILIATIONS_COLUMNS.map((column) => {
        return column.isMandatory === 'Y' && column.dataIndex;
      })
    );
  }, [selectedCustomer?.value]);

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    if (
      value === 'hcpName' ||
      value === 'hcpAddress' ||
      value === 'hcpCity' ||
      value === 'hcpState' ||
      value === 'hcpZip' ||
      value === 'hcpId' ||
      value === 'stakeholderType'
    ) {
      dispatch(setHcpFlag(checked));
    }
    if (
      value === 'hcpCity' ||
      value === 'hcpZip' ||
      value === 'hcpId' ||
      value === 'siteHin' ||
      value === 'siteDea' ||
      value === 'zip' ||
      value === 'siteState' ||
      value === 'siteCity'
    ) {
      checked ? (size.x = size.x + 150) : (size.x = size.x - 150);
    } else {
      checked ? (size.x = size.x + 250) : (size.x = size.x - 250);
    }

    // Toggle selected columns
    const nextSelected = checked
      ? [...selectedColumns, value]
      : selectedColumns.filter((item) => item !== value);

    setSelectedColumns(nextSelected);
  };

  const columnSelectionPanel = (
    <Checkbox.Group
      defaultValue={selectedColumns}
      className={styles.checkboxGroup}
      key={Math.random()}>
      {AFFILIATIONS_COLUMNS.map((column) => (
        <div
          className={styles.checkboxWrapper}
          key={column.id}>
          <Checkbox
            className={styles.checkbox}
            key={column.dataIndex}
            value={column.dataIndex}
            onChange={handleCheckboxChange}>
            {column.title}
          </Checkbox>
        </div>
      ))}
    </Checkbox.Group>
  );

  const popoverContent = (
    <div className={styles.customPopup}>{columnSelectionPanel}</div>
  );

  return (
    <div id='Affiliations'>
      <Fade>
        <Row className='mb-2'>
          <Col xs={6}>
            <p className='title title__bold title__large mb-1'>Affiliations</p>
          </Col>
          <Col
            className='d-flex justify-content-end align-items-center'
            xs={6}>
            <Popover
              content={popoverContent}
              trigger='click'
              open={popoverVisible}
              onOpenChange={handlePopoverVisibleChange}>
              <i
                className={`icon icon__affiliations_standardTable_settings align-center me-3`}
              />
            </Popover>
            <OverlayIcon
              tooltip={'Reset Filters'}
              onClick={resetFilters}
              className={`icon icon__reset-filters align-center me-3`}
            />
            <OverlayTrigger overlay={exportTooltip}>
              <i
                onClick={dowloadData}
                className={`icon icon__affiliations_export_excel1 align-center`}
              />
            </OverlayTrigger>
          </Col>
        </Row>
      </Fade>

      <AffiliationsFilters
        site={account}
        applyFilter={(filterExp) => handleFilterApply(filterExp)}
      />

      <Row>
        <Col>
          <Spin spinning={isLoading || isExporting}>
            <ConfigurableTable
              rows={affiliationData?.values || []}
              columns={getRestrictedColumn(
                TABLE_NAME_AFFILIATION,
                filteredColumns,
                columnRestriction
              ).map((col) => {
                if (col.dataIndex === 'siteName') {
                  return {
                    ...col,
                    render: (text, record) => (
                      <span
                        className='span-link'
                        onClick={() => {
                          handleClick(record);
                        }}>
                        {text}
                      </span>
                    ),
                  };
                }
                return col;
              })}
              expandRowByClick={true}
              enableColumnSelection={false}
              enableExport={false}
              exportFileName='affiliations'
              isGlobalExport={true}
              tableName={TABLE_NAME_AFFILIATION}
              pagination={pagination}
              isGlobalSort={true}
              onSort={handleTableChange}
              filterExp={filterExp}
              dimentions={size}
            />
          </Spin>
        </Col>
      </Row>
    </div>
  );
};

export default Affiliations;
